import { html } from "lit";
import { customElement } from "lit/decorators.js";

import "ui-components/form-elements/input/wow-input";
import "ui-components/form-elements/select/wow-select";
import "ui-components/collapsible-section/collapsible-section";
import "@phosphor-icons/webcomponents/PhCalendarBlank";
import "@phosphor-icons/webcomponents/PhClock";
import "@phosphor-icons/webcomponents/PhGlobeSimple";

import { styles } from "./live-event-basic-info.css";
import { SignalElement, inject } from "ui-common/di";
import type { ILiveEventFormState, ILiveEventFormService } from "ui-common/services/live-event-form/types";
import { numericKeydown, handleFormInput, handleFormBlur, getTodayDate } from "ui-common/form/form-utils";
import { t } from "../../../translations/i18n";

@customElement("live-event-basic-info")
export class LiveEventBasicInfo extends SignalElement {
  static override styles = styles;

  @inject<ILiveEventFormState>("liveEventFormState")
  private state!: ILiveEventFormState;

  @inject<ILiveEventFormService>("liveEventFormService")
  private service!: ILiveEventFormService;

  override render() {
    const formData = this.state.formData.get();
    const errors = this.state.validationErrors.get();

    return html`
      <wow-collapsible-section
        collapsible
        expanded
        header=${t("createLiveEventPage.basicInfo")}
        subtitle=${t("createLiveEventPage.basicInfoSubtitle")}
      >
        <div class="form-section">
          <div class="form-row">
            <div class="input-icon-wrapper">
              <wow-input
                label=${t("createLiveEventPage.date")}
                type="date"
                .value=${formData.scheduledDate}
                min=${getTodayDate()}
                error-message=${errors.scheduledDate || ""}
                @input=${(e: Event) => handleFormInput(this.service, "scheduledDate", e)}
                @blur=${() => handleFormBlur(this.service, "scheduledDate")}
              ></wow-input>
              <ph-calendar-blank class="input-icon" size="20"></ph-calendar-blank>
            </div>
            <div class="input-icon-wrapper">
              <wow-input
                label=${t("createLiveEventPage.time")}
                type="time"
                .value=${formData.scheduledTime}
                error-message=${errors.scheduledTime || ""}
                @input=${(e: Event) => handleFormInput(this.service, "scheduledTime", e)}
                @blur=${() => handleFormBlur(this.service, "scheduledTime")}
              ></wow-input>
              <ph-clock class="input-icon" size="20"></ph-clock>
            </div>
          </div>

          <div class="form-row">
            <wow-input
              label=${t("createLiveEventPage.expectedDuration")}
              type="number"
              inputmode="numeric"
              suffix=${t("createLiveEventPage.minutes")}
              min="1"
              max="1440"
              step="1"
              .value=${formData.durationMinutes}
              @input=${(e: Event) => handleFormInput(this.service, "durationMinutes", e)}
              @blur=${() => handleFormBlur(this.service, "durationMinutes")}
              @keydown=${numericKeydown}
            ></wow-input>
          </div>

          <div class="form-row">
            <wow-select
              label=${t("createLiveEventPage.language")}
              name="language"
              .options=${[
                { value: "nl", label: "Nederlands" },
                { value: "en", label: "English" },
              ]}
              .value=${formData.language}
              @change=${(e: CustomEvent) => this.service.updateFormData("language", e.detail.value)}
            >
              <ph-globe-simple slot="prefix" size="20"></ph-globe-simple>
            </wow-select>
          </div>
        </div>
      </wow-collapsible-section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "live-event-basic-info": LiveEventBasicInfo;
  }
}
